import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { X, Play, CheckCircle2 } from "lucide-react";

const WorkoutTimer = ({ onClose, initialWork = 20, initialRest = 10, initialRounds = 8, exerciseName = null }) => {
  const [mode, setMode] = useState("tabata"); // tabata, stopwatch, countdown
  const [workTime, setWorkTime] = useState(initialWork);
  const [restTime, setRestTime] = useState(initialRest);
  const [rounds, setRounds] = useState(initialRounds);
  const [countdownTime, setCountdownTime] = useState(60);
  const [currentRound, setCurrentRound] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [stopwatchTime, setStopwatchTime] = useState(0);
  const [isWork, setIsWork] = useState(true);
  const [isRunning, setIsRunning] = useState(false);
  const [isFinished, setIsFinished] = useState(false);
  const [countdownStart, setCountdownStart] = useState(null);
  const timerRef = useRef(null);
  const countdownRef = useRef([]);

  const playBeep = (freq = 880) => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = freq;
      osc.type = 'sine';
      gain.gain.setValueAtTime(0.3, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.3);
    } catch (e) {}
  };

  const triggerCountdown = (callback) => {
    countdownRef.current.forEach(t => clearTimeout(t));
    setCountdownStart(3);
    countdownRef.current = [
      setTimeout(() => setCountdownStart(2), 800),
      setTimeout(() => setCountdownStart(1), 1600),
      setTimeout(() => { setCountdownStart("GO"); playBeep(880); }, 2400),
      setTimeout(() => { setCountdownStart(null); callback(); }, 3200),
    ];
  };

  const startTabata = () => {
    setCurrentRound(1);
    setCurrentTime(workTime);
    setIsWork(true);
    setIsRunning(true);
    setIsFinished(false);
    playBeep();
  };

  const startStopwatch = () => {
    setStopwatchTime(0);
    setIsRunning(true);
    setIsFinished(false);
  };

  const startCountdown = () => {
    setCurrentTime(countdownTime);
    setIsRunning(true);
    setIsFinished(false);
  };

  const stop = () => {
    setIsRunning(false);
    clearInterval(timerRef.current);
  };

  const reset = () => {
    stop();
    setCurrentRound(0);
    setCurrentTime(0);
    setStopwatchTime(0);
    setIsWork(true);
    setIsFinished(false);
  };

  useEffect(() => {
    if (!isRunning) return;

    if (mode === "stopwatch") {
      timerRef.current = setInterval(() => {
        setStopwatchTime(prev => prev + 1);
      }, 1000);
    } else if (mode === "countdown") {
      timerRef.current = setInterval(() => {
        setCurrentTime(prev => {
          if (prev <= 1) {
            playBeep(660);
            setIsRunning(false);
            setIsFinished(true);
            clearInterval(timerRef.current);
            return 0;
          }
          if (prev <= 4) playBeep(440);
          return prev - 1;
        });
      }, 1000);
    } else {
      timerRef.current = setInterval(() => {
        setCurrentTime(prev => {
          if (prev <= 1) {
            playBeep(isWork ? 440 : 880);
            if (isWork) {
              setIsWork(false);
              return restTime;
            } else {
              if (currentRound >= rounds) {
                setIsRunning(false);
                setIsFinished(true);
                clearInterval(timerRef.current);
                return 0;
              }
              setCurrentRound(r => r + 1);
              setIsWork(true);
              return workTime;
            }
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => clearInterval(timerRef.current);
  }, [isRunning, isWork, currentRound, workTime, restTime, rounds, mode]);

  const formatTime = (s) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;

  return (
    <div className="timer-overlay" data-testid="workout-timer">
      {countdownStart !== null && (
        <div className="countdown-overlay">
          {typeof countdownStart === 'number' ? (
            <span key={countdownStart} className="countdown-number">{countdownStart}</span>
          ) : (
            <span key="go" className="countdown-go">{countdownStart}</span>
          )}
        </div>
      )}
      <div className="timer-container">
        <button className="timer-close" onClick={onClose}><X size={24} /></button>

        {exerciseName && <div className="timer-exercise-name">{exerciseName}</div>}

        {!isRunning && currentRound === 0 && stopwatchTime === 0 && !isFinished ? (
          <div className="timer-setup">
            <div className="timer-modes">
              <Button variant={mode === "tabata" ? "default" : "outline"} size="sm" onClick={() => setMode("tabata")}>Tabata</Button>
              <Button variant={mode === "stopwatch" ? "default" : "outline"} size="sm" onClick={() => setMode("stopwatch")}>Stoper</Button>
              <Button variant={mode === "countdown" ? "default" : "outline"} size="sm" onClick={() => setMode("countdown")}>Odliczanie</Button>
            </div>

            {mode === "tabata" && (
              <>
                <div className="timer-settings">
                  <div className="timer-setting">
                    <label>Praca (s)</label>
                    <Input type="number" value={workTime} onChange={(e) => setWorkTime(Number(e.target.value))} min={5} max={120} />
                  </div>
                  <div className="timer-setting">
                    <label>Przerwa (s)</label>
                    <Input type="number" value={restTime} onChange={(e) => setRestTime(Number(e.target.value))} min={5} max={120} />
                  </div>
                  <div className="timer-setting">
                    <label>Rundy</label>
                    <Input type="number" value={rounds} onChange={(e) => setRounds(Number(e.target.value))} min={1} max={20} />
                  </div>
                </div>
                <div className="timer-presets">
                  <Button variant="outline" size="sm" onClick={() => { setWorkTime(20); setRestTime(10); setRounds(8); }}>20/10 x8</Button>
                  <Button variant="outline" size="sm" onClick={() => { setWorkTime(30); setRestTime(15); setRounds(8); }}>30/15 x8</Button>
                  <Button variant="outline" size="sm" onClick={() => { setWorkTime(45); setRestTime(15); setRounds(6); }}>45/15 x6</Button>
                </div>
                <Button className="timer-start" onClick={() => triggerCountdown(startTabata)}><Play size={20} /> Start Tabata</Button>
              </>
            )}

            {mode === "stopwatch" && (
              <Button className="timer-start" onClick={() => triggerCountdown(startStopwatch)}><Play size={20} /> Start Stoper</Button>
            )}

            {mode === "countdown" && (
              <>
                <div className="timer-countdown-input">
                  <label>Czas (s)</label>
                  <Input type="number" value={countdownTime} onChange={(e) => setCountdownTime(Number(e.target.value))} min={10} max={3600} />
                </div>
                <div className="timer-presets">
                  <Button variant="outline" size="sm" onClick={() => setCountdownTime(30)}>30s</Button>
                  <Button variant="outline" size="sm" onClick={() => setCountdownTime(60)}>1 min</Button>
                  <Button variant="outline" size="sm" onClick={() => setCountdownTime(180)}>3 min</Button>
                  <Button variant="outline" size="sm" onClick={() => setCountdownTime(300)}>5 min</Button>
                </div>
                <Button className="timer-start" onClick={() => triggerCountdown(startCountdown)}><Play size={20} /> Start</Button>
              </>
            )}
          </div>
        ) : isFinished ? (
          <div className="timer-finished">
            <CheckCircle2 size={64} />
            <h2>Gotowe!</h2>
            {mode === "tabata" && <p>{rounds} rund ukończonych</p>}
            <Button onClick={reset}>Nowa sesja</Button>
          </div>
        ) : (
          <div className={`timer-active ${mode === "tabata" ? (isWork ? 'work' : 'rest') : ''}`}>
            {mode === "tabata" && <div className="timer-phase">{isWork ? 'PRACA' : 'PRZERWA'}</div>}
            <div className="timer-time">
              {mode === "stopwatch" ? formatTime(stopwatchTime) : formatTime(currentTime)}
            </div>
            {mode === "tabata" && <div className="timer-round">Runda {currentRound}/{rounds}</div>}
            <div className="timer-controls">
              <Button variant="outline" onClick={stop}><X size={18} /> Stop</Button>
              <Button variant="outline" onClick={reset}>Reset</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WorkoutTimer;
